import type { Cleanup } from '../helpers/cleanup'
import type { ViewHook } from '../helpers/utils'

type PaginationHookContext = ViewHook & {
  _busy?: boolean
  _cleanup?: Cleanup
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object'

const pageFromEvent = (event: Event): number | undefined => {
  const detail = 'detail' in event ? event.detail : undefined

  if (!isRecord(detail)) {
    return undefined
  }

  const page = detail.page

  return typeof page === 'number' && Number.isSafeInteger(page) && page > 0
    ? page
    : undefined
}

const setDisabled = (element: HTMLElement, disabled: boolean): void => {
  element.toggleAttribute('disabled', disabled)
}

const setBusy = (context: PaginationHookContext, busy: boolean): void => {
  context._busy = busy

  if (busy) {
    context.el.setAttribute('aria-busy', 'true')
  } else {
    context.el.removeAttribute('aria-busy')
  }

  setDisabled(context.el, busy || context.el.dataset.facadeDisabled === 'true')
}

const cleanup = (context: PaginationHookContext): void => {
  context._cleanup?.()
  context._cleanup = undefined
}

export const FacadePagination = {
  mounted(this: PaginationHookContext): void {
    cleanup(this)
    setBusy(this, false)

    const handlePageChange = (event: Event): void => {
      const page = pageFromEvent(event)

      if (page === undefined || this._busy === true) {
        return
      }

      setBusy(this, true)

      try {
        this.pushEvent('paginate', { page })
      } catch (error) {
        setBusy(this, false)
        throw error
      }
    }

    this.el.addEventListener('wa-page-change', handlePageChange)
    this._cleanup = () =>
      this.el.removeEventListener('wa-page-change', handlePageChange)
  },

  updated(this: PaginationHookContext): void {
    setBusy(this, false)
  },

  disconnected(this: PaginationHookContext): void {
    setDisabled(this.el, true)
  },

  reconnected(this: PaginationHookContext): void {
    setBusy(this, false)
  },

  destroyed(this: PaginationHookContext): void {
    cleanup(this)
    setBusy(this, false)
  }
}
